import * as React from "react";
import { Link } from "react-router-dom";
import { Package, AlertTriangle, Truck, CheckCircle2 } from "lucide-react";

import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useOrders } from "@/hooks/useOrders";
import { useAppLocale } from "@/lib/i18n";
import { formatStatusLabel } from "@/lib/format";
import { isHiddenInInternalOrderTablesByDefault } from "@/lib/order-status-visibility";
import { getOrderStatusBadgeClass } from "@/lib/status-tag-colors";
import { m } from "@/paraglide/messages.js";

function getOrderTabHref(status: string): string | null {
  if (status === "awaiting_inventory") return "/logistics/orders?status=awaiting_inventory";
  if (status === "ready_to_prepare") return "/logistics/orders?status=ready_to_prepare";
  if (status === "ready_for_pickup") return "/logistics/orders?status=ready_for_pickup";
  if (status === "in_transit") return "/logistics/orders?status=in_transit";
  if (status === "delivered") return "/logistics/orders?status=delivered";
  if (status === "on_hold") return "/logistics/orders?status=on_hold";
  if (status === "cancelled") return "/logistics/orders?status=cancelled";
  return null;
}

function getPipelineStatusLabel(status: string): string {
  if (status === "awaiting_inventory") return "Awaiting Inventory";
  if (status === "ready_to_prepare") return m.status_ready_to_pull();
  return formatStatusLabel(status);
}

function getDisplayedOrderStatus(order: any): string {
  return String(order?.operationalStatus ?? order?.status ?? "").trim();
}

export default function LogisticsDashboardPage() {
  const { locale } = useAppLocale();
  const orders = useOrders(1000);

  const stats = React.useMemo(() => {
    if (!orders) return { active: 0, attention: 0, inTransit: 0, delivered30d: 0 };
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
    const active = orders.filter((o: any) => !["delivered", "cancelled"].includes(getDisplayedOrderStatus(o))).length;
    const attention = orders.filter((o: any) => {
      const status = getDisplayedOrderStatus(o);
      return status === "on_hold" || status === "awaiting_inventory" || status === "ready_to_prepare";
    }).length;
    const inTransit = orders.filter((o: any) => getDisplayedOrderStatus(o) === "in_transit").length;
    const delivered30d = orders.filter((o: any) => getDisplayedOrderStatus(o) === "delivered" && o._creationTime > thirtyDaysAgo).length;
    return { active, attention, inTransit, delivered30d };
  }, [orders]);

  const pipeline = React.useMemo(() => {
    if (!orders) return [];
    const counts: Record<string, number> = {};
    for (const o of orders) {
      const status = getDisplayedOrderStatus(o);
      counts[status] = (counts[status] || 0) + 1;
    }
    return Object.entries(counts)
      .filter(([status]) => !isHiddenInInternalOrderTablesByDefault(status))
      .sort(([a], [b]) => {
        const order = ["pending_payment", "payment_processing", "awaiting_inventory", "ready_to_prepare", "ready_for_pickup", "in_transit", "delivered", "on_hold", "cancelled"];
        return order.indexOf(a) - order.indexOf(b);
      });
  }, [orders]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{m.logistics_dashboard_title()}</h1>
          <p className="text-muted-foreground mt-1">{m.logistics_dashboard_description()}</p>
        </div>
        <Button asChild>
          <Link to="/logistics/orders">{m.logistics_view_all_orders()}</Link>
        </Button>
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InternalMetricCard title={m.metric_active_orders()} value={String(stats.active)} hint={m.metric_active_orders_hint()} icon={Package} />
        <InternalMetricCard title={m.metric_needs_attention()} value={String(stats.attention)} hint={m.metric_needs_attention_hint()} icon={AlertTriangle} />
        <InternalMetricCard title={m.metric_in_transit()} value={String(stats.inTransit)} hint={m.metric_in_transit_hint()} icon={Truck} />
        <InternalMetricCard title={m.metric_delivered_30d()} value={String(stats.delivered30d)} hint={m.metric_delivered_30d_hint()} icon={CheckCircle2} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{m.logistics_order_pipeline_title()}</CardTitle>
          <CardDescription>{m.logistics_order_pipeline_description()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
            {pipeline.map(([status, count]) => {
              const tabHref = getOrderTabHref(status);

              if (tabHref) {
                return (
                  <Link
                    key={status}
                    to={tabHref}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/40"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getOrderStatusBadgeClass(status)}>
                        {getPipelineStatusLabel(status)}
                      </Badge>
                    </div>
                    <span className="text-xl font-bold">{count}</span>
                  </Link>
                );
              }

              return (
                <div key={status} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={getOrderStatusBadgeClass(status)}>
                      {getPipelineStatusLabel(status)}
                    </Badge>
                  </div>
                  <span className="text-xl font-bold">{count}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
